/**
 * The builder's client-side draft — what the form edits and what autosaves —
 * and the pure conversions to and from the server request. The client never
 * prices anything: the only arithmetic here is the "≈ $X of $Y" share hint.
 */

import type {
  GeoScope,
  GuardrailFinding,
  GuardrailKind,
  MediaPlanAgency,
  MediaPlanDoc,
  MediaPlanDraftDoc,
  MediaPlanPricingOverrides,
  MediaPlanRequest,
  MediaPlanSegmentRequest,
  MediaPlanTargeting,
  MediaPlanUnitKey,
  PlanInput,
  UpchargesMode,
} from './types';
import { MEDIA_PLAN_UNIT_ORDER } from './types';
import { DIRECT_AGENCY_LIST_KEY, MAX_SEGMENTS, MAX_TIERS } from './vocab';

/** Agency select values besides an agency key. */
export const DIRECT_AGENCY = '__direct__';
export const OTHER_AGENCY = '__other__';

/** An agency key as the builder's picker holds it: none, or the agency list's own "Direct (no agency)" entry, is DIRECT_AGENCY. */
export const draftAgencyKey = (key: string | undefined): string => (!key || key === DIRECT_AGENCY_LIST_KEY ? DIRECT_AGENCY : key);

/** The server's limits (backend mediaPlanEngine.ts), checked here so a quote is never sent to fail. */
export const MAX_BUDGET_USD = 10_000_000;
export const MAX_FLIGHT_DAYS = 731;
export const MAX_RATE_CPM = 1000;
/** A flagged rate's internal reason must be 10–500 characters (backend PRICING_REASON_CHARS). */
export const REASON_MIN_CHARS = 10;
export const REASON_MAX_CHARS = 500;

/** A rate the seller set for one audience × ad unit. Units without one take the server's starting rate. */
export interface RateEdit {
  /** As typed ("24", "24.50"); '' while the field is being cleared. */
  value: string;
  /** 'adopted' = picked from "Why this rate?"; 'prior' = the seller's own rate on the version this one revises. */
  origin: 'typed' | 'adopted' | 'prior';
}

export interface SegmentDraft {
  /** Stable id for React keys, rates and reasons; the request key is slugified from the label. */
  id: string;
  label: string;
  description: string;
  /** '' = equal split; otherwise a percent string. */
  budgetPct: string;
  demo: string[];
  cultureStacks: string[];
  geoScope: GeoScope;
  /** One DMA / state / ZIP per line, or comma separated. */
  geoValuesText: string;
  dayparts: string[];
  competitiveConquesting: string[];
  useOwnMix: boolean;
  unitMix: Partial<Record<MediaPlanUnitKey, string>>;
  rates: Partial<Record<MediaPlanUnitKey, RateEdit>>;
}

export interface PlanDraft {
  advertiserKey: string;
  advertiserName: string;
  /** DIRECT_AGENCY, OTHER_AGENCY (free text in agencyOther) or an agency key. */
  agencyKey: string;
  agencyOther: string;
  objective: string;
  budgetUsd: string;
  tiersUsd: string[];
  flightStart: string;
  flightEnd: string;
  notes: string;
  useCardMix: boolean;
  unitMix: Partial<Record<MediaPlanUnitKey, string>>;
  segments: SegmentDraft[];
  /** How the seller's own rates treat targeting: 'none' = already included; 'card' = list upcharges go on top. */
  upchargesMode: UpchargesMode;
  includeAddedValue: boolean;
  /** Percent string, '' = card policy. */
  addedValuePct: string;
  roundImpressionsTo: 1 | 1000;
  /** Internal reasons for flagged rates, by `reasonKey()`. Never on a client file. */
  reasons: Record<string, string>;
  /** "New version": the version revised and its net rates by audience id, for "v1 was $X" hints. */
  prior?: { version: number; rates: Record<string, Partial<Record<MediaPlanUnitKey, number>>> };
}

let seq = 0;
export const nextSegmentId = (): string => `seg-${Date.now().toString(36)}-${(seq++).toString(36)}`;

export function newSegmentDraft(label = 'General Market'): SegmentDraft {
  return {
    id: nextSegmentId(),
    label,
    description: '',
    budgetPct: '',
    demo: [],
    cultureStacks: [],
    geoScope: 'national',
    geoValuesText: '',
    dayparts: [],
    competitiveConquesting: [],
    useOwnMix: false,
    unitMix: {},
    rates: {},
  };
}

export function newDraft(): PlanDraft {
  return {
    advertiserKey: '',
    advertiserName: '',
    agencyKey: DIRECT_AGENCY,
    agencyOther: '',
    objective: '',
    budgetUsd: '',
    tiersUsd: [],
    flightStart: '',
    flightEnd: '',
    notes: '',
    useCardMix: true,
    unitMix: { 'ns-15s': '50', 'ctv-30s': '50' },
    segments: [newSegmentDraft()],
    upchargesMode: 'none',
    includeAddedValue: true,
    addedValuePct: '',
    roundImpressionsTo: 1,
    reasons: {},
  };
}

export function slugifySegmentKey(label: string): string {
  const slug = label
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
  return slug || 'segment';
}

/** Segment keys for a draft — slugs from labels, made unique with -2, -3 …. */
export function segmentKeysFor(segments: readonly SegmentDraft[]): string[] {
  const used = new Set<string>();
  return segments.map((s) => {
    const base = slugifySegmentKey(s.label);
    let key = base;
    let n = 2;
    while (used.has(key)) key = `${base.slice(0, 36)}-${n++}`;
    used.add(key);
    return key;
  });
}

/** Request segment key → the audience's draft id. */
export function segmentIdsByKey(segments: readonly SegmentDraft[]): Map<string, string> {
  const keys = segmentKeysFor(segments);
  return new Map(keys.map((key, i) => [key, segments[i].id]));
}

export function parseGeoValues(text: string): string[] {
  return Array.from(new Set(text.split(/[\n,;]+/).map((v) => v.trim()).filter(Boolean)));
}

export function parseNumber(s: string): number | null {
  const cleaned = s.replace(/[$,%\s]/g, '');
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** A dollar amount as the field shows it once the seller leaves it: "250000" → "250,000". Text that isn't a number stays as typed. */
export function formatAmount(s: string): string {
  const n = parseNumber(s);
  return n === null ? s : n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function parseMix(mix: Partial<Record<MediaPlanUnitKey, string>>): Partial<Record<MediaPlanUnitKey, number>> {
  const out: Partial<Record<MediaPlanUnitKey, number>> = {};
  for (const key of MEDIA_PLAN_UNIT_ORDER) {
    const n = parseNumber(mix[key] ?? '');
    if (n !== null && n > 0) out[key] = n;
  }
  return out;
}

export function mixTotal(mix: Partial<Record<MediaPlanUnitKey, string>>): number {
  return Object.values(parseMix(mix)).reduce((s, v) => s + v, 0);
}

/** A typed rate as a CPM the server accepts, or null (blank or out of range). */
export function parseRate(value: string): number | null {
  const n = parseNumber(value);
  return n !== null && n > 0 && n <= MAX_RATE_CPM ? Math.round(n * 100) / 100 : null;
}

/** Percent each segment gets: explicit when every segment sets one, else equal. Null when mixed/invalid. */
export function segmentShares(segments: readonly SegmentDraft[]): number[] | null {
  const given = segments.map((s) => parseNumber(s.budgetPct));
  if (given.every((g) => g === null)) return segments.map(() => 100 / Math.max(1, segments.length));
  if (given.some((g) => g === null)) return null;
  return given as number[];
}

export function remainingSegmentPct(segments: readonly SegmentDraft[]): number {
  const given = segments.map((s) => parseNumber(s.budgetPct) ?? 0);
  return Math.round((100 - given.reduce((s, v) => s + v, 0)) * 100) / 100;
}

/** "≈ $X of $Y" hint. */
export function segmentBudgetUsd(budgetUsd: string, pct: number | null): number | null {
  const budget = parseNumber(budgetUsd);
  if (budget === null || pct === null) return null;
  return Math.round(budget * pct) / 100;
}

export function targetingFromSegment(s: SegmentDraft): MediaPlanTargeting {
  const values = s.geoScope === 'national' ? [] : parseGeoValues(s.geoValuesText);
  return {
    geo: { scope: s.geoScope, values: values.length ? values : undefined },
    demo: s.demo.length ? s.demo : undefined,
    cultureStacks: s.cultureStacks.length ? s.cultureStacks : undefined,
    dayparts: s.dayparts.length ? s.dayparts : undefined,
    competitiveConquesting: s.competitiveConquesting.length ? s.competitiveConquesting : undefined,
  };
}

// --- Validation ------------------------------------------------------------------------

export type DraftSection = 'who' | 'budget' | 'units' | 'audiences' | 'rates' | 'addedValue';

/** Something to fix before the brief can be priced. The field's input has id `fieldId(field)`. */
export interface DraftIssue {
  field: string;
  section: DraftSection;
  message: string;
}

/** The DOM id of a builder field, so a check can move focus to it. */
export const fieldId = (field: string): string => `mp-${field}`;

export const segmentField = (segmentId: string, part: 'label' | 'share' | 'geo' | 'mix'): string => `segment-${segmentId}-${part}`;
export const rateField = (segmentId: string, unitKey: MediaPlanUnitKey): string => `rate-${segmentId}-${unitKey}`;

const isoDate = (s: string): Date | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(`${s}T00:00:00Z`);
  return Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== s ? null : d;
};

const pctText = (n: number): string => `${Math.round(n * 100) / 100}%`;

const geoNoun = (scope: GeoScope): string => (scope === 'dma' ? 'DMAs' : scope === 'state' ? 'states' : 'ZIP codes');

/** Everything that stops the draft from being priced, in form order. Empty = ready to quote. */
export function validateDraft(d: PlanDraft): DraftIssue[] {
  const issues: DraftIssue[] = [];
  const add = (field: string, section: DraftSection, message: string) => issues.push({ field, section, message });

  if (!d.advertiserName.trim()) add('advertiser', 'who', 'Add the advertiser.');
  if (d.agencyKey === OTHER_AGENCY && !d.agencyOther.trim()) add('agencyOther', 'who', "Type the agency's name.");

  const budget = parseNumber(d.budgetUsd);
  if (!d.budgetUsd.trim()) add('budget', 'budget', 'Enter the budget.');
  else if (budget === null || budget <= 0) add('budget', 'budget', 'The budget must be an amount above $0.');
  else if (budget > MAX_BUDGET_USD) add('budget', 'budget', 'The budget can be at most $10,000,000.');
  d.tiersUsd.forEach((t, i) => {
    if (!t.trim()) return;
    const n = parseNumber(t);
    if (n === null || n <= 0) add(`tier-${i}`, 'budget', `Budget option ${i + 2} must be an amount above $0.`);
    else if (n > MAX_BUDGET_USD) add(`tier-${i}`, 'budget', `Budget option ${i + 2} can be at most $10,000,000.`);
  });
  if (d.tiersUsd.filter((t) => t.trim()).length > MAX_TIERS - 1) add('tiers', 'budget', `A plan can have at most ${MAX_TIERS} budget options.`);

  const start = isoDate(d.flightStart);
  const end = isoDate(d.flightEnd);
  if (!start) add('flightStart', 'budget', 'Choose the flight start.');
  if (!end) add('flightEnd', 'budget', 'Choose the flight end.');
  if (start && end) {
    if (end < start) add('flightEnd', 'budget', 'The flight ends before it starts.');
    else if ((end.getTime() - start.getTime()) / 86_400_000 + 1 > MAX_FLIGHT_DAYS) add('flightEnd', 'budget', 'A flight can run at most 2 years.');
  }

  if (!d.useCardMix) {
    const total = mixTotal(d.unitMix);
    if (Math.abs(total - 100) > 0.01) add('unitMix', 'units', `The unit mix adds up to ${pctText(total)}, not 100%.`);
  }

  if (d.segments.length === 0) add('segments', 'audiences', 'Add an audience.');
  if (d.segments.length > MAX_SEGMENTS) add('segments', 'audiences', `A plan can have at most ${MAX_SEGMENTS} audiences.`);
  const multi = d.segments.length > 1;
  const shares = segmentShares(d.segments);
  d.segments.forEach((s, i) => {
    const name = s.label.trim() || `Audience ${i + 1}`;
    if (!s.label.trim()) add(segmentField(s.id, 'label'), 'audiences', `Name audience ${i + 1}.`);
    if (multi && shares === null && !s.budgetPct.trim()) {
      add(segmentField(s.id, 'share'), 'audiences', `Give ${name} a budget share, or clear every share for an equal split.`);
    }
    const share = parseNumber(s.budgetPct);
    if (multi && s.budgetPct.trim() && (share === null || share <= 0 || share > 100)) {
      add(segmentField(s.id, 'share'), 'audiences', `${name}'s budget share must be above 0% and at most 100%.`);
    }
    if (s.geoScope !== 'national' && parseGeoValues(s.geoValuesText).length === 0) {
      add(segmentField(s.id, 'geo'), 'audiences', `List the ${geoNoun(s.geoScope)} ${name} targets.`);
    }
    if (s.useOwnMix) {
      const total = mixTotal(s.unitMix);
      if (Math.abs(total - 100) > 0.01) add(segmentField(s.id, 'mix'), 'audiences', `${name}'s unit mix adds up to ${pctText(total)}, not 100%.`);
    }
  });
  if (multi && shares !== null && d.segments.some((s) => s.budgetPct.trim())) {
    const total = shares.reduce((s, v) => s + v, 0);
    if (Math.abs(total - 100) > 0.01) add(segmentField(d.segments[0].id, 'share'), 'audiences', `The audience shares add up to ${pctText(total)}, not 100%.`);
  }

  d.segments.forEach((s) => {
    for (const unitKey of MEDIA_PLAN_UNIT_ORDER) {
      const edit = s.rates[unitKey];
      if (edit && edit.value.trim() && parseRate(edit.value) === null) {
        add(rateField(s.id, unitKey), 'rates', `Enter a rate from $0.01 to $${MAX_RATE_CPM.toLocaleString('en-US')}.`);
      }
    }
  });

  if (d.addedValuePct.trim()) {
    const pct = parseNumber(d.addedValuePct);
    if (pct === null || pct < 0 || pct > 100) add('addedValuePct', 'addedValue', 'Added value must be from 0% to 100%.');
  }
  return issues;
}

// --- Draft → request -----------------------------------------------------------------

/** The seller's own rates as request.pricing (none = every rate is the server's starting rate). */
export function pricingFromDraft(d: PlanDraft, keys: readonly string[] = segmentKeysFor(d.segments)): MediaPlanPricingOverrides | undefined {
  const segmentUnitCpm: Record<string, Partial<Record<MediaPlanUnitKey, number>>> = {};
  d.segments.forEach((s, i) => {
    const rates: Partial<Record<MediaPlanUnitKey, number>> = {};
    for (const unitKey of MEDIA_PLAN_UNIT_ORDER) {
      const rate = parseRate(s.rates[unitKey]?.value ?? '');
      if (rate !== null) rates[unitKey] = rate;
    }
    if (Object.keys(rates).length) segmentUnitCpm[keys[i]] = rates;
  });
  return Object.keys(segmentUnitCpm).length ? { segmentUnitCpm, upchargesMode: d.upchargesMode } : undefined;
}

function agencyFields(d: PlanDraft): Pick<PlanInput, 'agencyKey' | 'agency' | 'noAgency'> {
  if (d.agencyKey === OTHER_AGENCY) return d.agencyOther.trim() ? { agency: d.agencyOther.trim() } : { noAgency: true };
  if (d.agencyKey === DIRECT_AGENCY || !d.agencyKey) return { noAgency: true };
  return { agencyKey: d.agencyKey };
}

/**
 * The body for POST /plans/quote and POST /plans. Every audience goes as a
 * segment — a single one too — so its name and description reach the plan.
 */
export function draftToPlanInput(d: PlanDraft): PlanInput {
  const budget = parseNumber(d.budgetUsd) ?? 0;
  const tiers = d.tiersUsd.map((t) => parseNumber(t)).filter((n): n is number => n !== null && n > 0 && Math.abs(n - budget) > 0.005);
  const keys = segmentKeysFor(d.segments);
  const shares = segmentShares(d.segments);
  const explicitShares = d.segments.length > 1 && d.segments.some((s) => s.budgetPct.trim());
  const segments: MediaPlanSegmentRequest[] = d.segments.map((s, i) => ({
    key: keys[i],
    label: s.label.trim(),
    description: s.description.trim() || undefined,
    budgetPct: explicitShares && shares ? shares[i] : undefined,
    targeting: targetingFromSegment(s),
    unitMix: s.useOwnMix ? parseMix(s.unitMix) : undefined,
  }));
  const addedValuePct = parseNumber(d.addedValuePct);
  return {
    advertiserName: d.advertiserName.trim(),
    advertiserKey: d.advertiserKey || undefined,
    ...agencyFields(d),
    budgetUsd: budget,
    tiersUsd: tiers.length ? Array.from(new Set(tiers)) : undefined,
    flightStart: d.flightStart,
    flightEnd: d.flightEnd,
    objective: d.objective.trim() || undefined,
    unitMix: d.useCardMix ? undefined : parseMix(d.unitMix),
    targeting: {},
    segments,
    pricing: pricingFromDraft(d, keys),
    includeAddedValue: d.includeAddedValue,
    addedValuePctOverride: addedValuePct !== null && d.addedValuePct.trim() ? addedValuePct / 100 : undefined,
    roundImpressionsTo: d.roundImpressionsTo,
    notes: d.notes.trim() || undefined,
  };
}

// --- Reasons for flagged rates -------------------------------------------------------

/** Below-list and below-history share one reason per line; above-list (the typo catch) needs its own. */
export type ReasonDirection = 'below' | 'above';

export const reasonDirection = (kind: GuardrailKind): ReasonDirection => (kind === 'above-list' ? 'above' : 'below');

export const reasonKey = (direction: ReasonDirection, segmentId: string, unitKey: MediaPlanUnitKey): string => `${direction}:${segmentId}:${unitKey}`;

export const reasonIsValid = (text: string | undefined): boolean => {
  const n = (text || '').trim().length;
  return n >= REASON_MIN_CHARS && n <= REASON_MAX_CHARS;
};

/** The draft key a server finding's reason is stored under, or null when its audience is gone. */
export function reasonKeyForFinding(f: Pick<GuardrailFinding, 'kind' | 'segmentKey' | 'unitKey'>, idsByKey: ReadonlyMap<string, string>): string | null {
  const segmentId = idsByKey.get(f.segmentKey);
  return segmentId ? reasonKey(reasonDirection(f.kind), segmentId, f.unitKey) : null;
}

/** Finding id → reason, for POST /plans `pricingNotes` (only reasons the server will accept). */
export function pricingNotesFor(d: PlanDraft, findings: readonly GuardrailFinding[]): Record<string, string> {
  const idsByKey = segmentIdsByKey(d.segments);
  const notes: Record<string, string> = {};
  for (const f of findings) {
    const key = reasonKeyForFinding(f, idsByKey);
    const text = key ? d.reasons[key] : undefined;
    if (text !== undefined && reasonIsValid(text)) notes[f.id] = text.trim();
  }
  return notes;
}

/** Findings whose line still needs a reason. */
export function findingsMissingReasons(d: PlanDraft, findings: readonly GuardrailFinding[]): GuardrailFinding[] {
  const notes = pricingNotesFor(d, findings);
  return findings.filter((f) => !notes[f.id]);
}

// --- Plan / chat draft → draft --------------------------------------------------------

const toStrMix = (mix?: Partial<Record<MediaPlanUnitKey, number>>): Partial<Record<MediaPlanUnitKey, string>> => {
  const out: Partial<Record<MediaPlanUnitKey, string>> = {};
  for (const [k, v] of Object.entries(mix || {})) if (typeof v === 'number') out[k as MediaPlanUnitKey] = String(v);
  return out;
};

function segmentFromTargeting(label: string, t: MediaPlanTargeting = {}): SegmentDraft {
  return {
    ...newSegmentDraft(label),
    demo: t.demo || [],
    cultureStacks: t.cultureStacks || [],
    geoScope: t.geo?.scope || 'national',
    geoValuesText: (t.geo?.values || []).join('\n'),
    dayparts: t.dayparts || [],
    competitiveConquesting: t.competitiveConquesting || [],
  };
}

const segmentFromRequest = (s: MediaPlanSegmentRequest): SegmentDraft => ({
  ...segmentFromTargeting(s.label, s.targeting),
  description: s.description || '',
  budgetPct: typeof s.budgetPct === 'number' ? String(Math.round(s.budgetPct * 100) / 100) : '',
  useOwnMix: !!s.unitMix && Object.keys(s.unitMix).length > 0,
  unitMix: toStrMix(s.unitMix),
});

/**
 * The request's audiences as drafts, plus a lookup from a request segment key
 * to the draft id. A plan without segments has one audience, keyed 'all'.
 */
function segmentsFromRequest(r: MediaPlanRequest, single?: { label?: string; description?: string }): { segments: SegmentDraft[]; idFor: (segmentKey: string | undefined) => string | undefined } {
  if (r.segments?.length) {
    const segments = r.segments.map(segmentFromRequest);
    const ids = new Map(r.segments.map((s, i) => [s.key, segments[i].id]));
    return { segments, idFor: (key) => (key ? ids.get(key) : undefined) };
  }
  const label = single?.label || (r.targeting?.demo?.length ? r.targeting.demo.join(' ') : 'General Market');
  const segment = { ...segmentFromTargeting(label, r.targeting), description: single?.description || '' };
  return { segments: [segment], idFor: () => segment.id };
}

const sharedFields = (
  r: MediaPlanRequest,
): Pick<PlanDraft, 'objective' | 'budgetUsd' | 'tiersUsd' | 'flightStart' | 'flightEnd' | 'notes' | 'useCardMix' | 'unitMix' | 'includeAddedValue' | 'addedValuePct' | 'roundImpressionsTo'> => ({
  objective: r.objective || '',
  budgetUsd: formatAmount(String(r.budgetUsd)),
  tiersUsd: (r.tiersUsd || []).map((t) => formatAmount(String(t))),
  flightStart: r.flightStart,
  flightEnd: r.flightEnd,
  notes: r.notes || '',
  useCardMix: !r.unitMix || Object.keys(r.unitMix).length === 0,
  unitMix: r.unitMix && Object.keys(r.unitMix).length ? toStrMix(r.unitMix) : { 'ns-15s': '50', 'ctv-30s': '50' },
  includeAddedValue: r.includeAddedValue !== false,
  addedValuePct: typeof r.addedValuePctOverride === 'number' ? String(Math.round(r.addedValuePctOverride * 10000) / 100) : '',
  roundImpressionsTo: r.roundImpressionsTo === 1000 ? 1000 : 1,
});

/**
 * Rebuild an editable draft from a saved plan ("New version"): the same
 * inputs, the agency as it was typed and the audiences' names. Rates the
 * seller set on that version come along (net, so targeting stays inside them)
 * with their reasons; the rest start from today's policy, and `prior` keeps
 * that version's rates so a row can say "v1 was $24.00" when its price moved.
 * `agencyLabel` is the route's label for plans saved before the doc stored its own.
 */
export function draftFromPlan(plan: MediaPlanDoc, opts: { agencyLabel?: string } = {}): PlanDraft {
  const r = plan.request;
  // A one-audience plan's name lives on its tier (the request carried no segments).
  const firstTier = plan.tiers?.[0];
  const single = firstTier?.segments?.length === 1 ? firstTier.segments[0] : undefined;
  const { segments, idFor } = segmentsFromRequest(r, single);
  const byId = new Map(segments.map((s) => [s.id, s]));

  const priorRates: Record<string, Partial<Record<MediaPlanUnitKey, number>>> = {};
  const setPrior = (segmentKey: string | undefined, unitKey: MediaPlanUnitKey, cpm: number) => {
    const id = idFor(segmentKey);
    if (id) priorRates[id] = { ...priorRates[id], [unitKey]: cpm };
  };
  const rows = (plan.rates || []).filter((row) => row.rateType === 'cpm' && row.effective);
  if (rows.length) {
    for (const row of rows) {
      const netCpm = row.effective!.netCpm;
      setPrior(row.segmentKey, row.unitKey, netCpm);
      const segment = byId.get(idFor(row.segmentKey) || '');
      if (segment && row.effective!.basis === 'seller') segment.rates[row.unitKey] = { value: String(netCpm), origin: 'prior' };
    }
  } else {
    // Plans priced before Oct 2026: their lines say what each audience paid.
    for (const line of firstTier?.lines || []) {
      if (line.kind === 'paid' && line.rateType === 'cpm' && line.netCpm > 0) setPrior(line.segmentKey, line.unitKey, line.netCpm);
    }
  }

  const reasons: Record<string, string> = {};
  for (const f of plan.pricingReview?.findings || []) {
    const text = plan.pricingReview?.reasons[f.id];
    const id = idFor(f.segmentKey);
    if (text && id) reasons[reasonKey(reasonDirection(f.kind), id, f.unitKey)] = text;
  }

  const agencyLabel = plan.agencyLabel || opts.agencyLabel || '';
  return {
    advertiserKey: plan.advertiser.key,
    advertiserName: plan.advertiser.name,
    // An agency that matched none in the list comes back as the text that was typed.
    agencyKey: plan.agencyKey ? draftAgencyKey(plan.agencyKey) : agencyLabel ? OTHER_AGENCY : DIRECT_AGENCY,
    agencyOther: plan.agencyKey ? '' : agencyLabel,
    ...sharedFields(r),
    segments,
    upchargesMode: 'none',
    reasons,
    prior: { version: plan.version, rates: priorRates },
  };
}

const sameName = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

/** The agency select's value for a typed agency name: a registry match by name or alias, else free text. */
export function agencyChoiceForName(name: string, agencies: readonly MediaPlanAgency[]): Pick<PlanDraft, 'agencyKey' | 'agencyOther'> {
  const match = agencies.find((a) => sameName(a.name, name) || a.aliases.some((alias) => sameName(alias, name)));
  return match ? { agencyKey: draftAgencyKey(match.key), agencyOther: '' } : { agencyKey: OTHER_AGENCY, agencyOther: name.trim() };
}

/**
 * A brief priced in chat, as a draft to review. Its rates are the ones chat
 * was asked for (a plan-wide rate applies to every audience); with no agency
 * named, the advertiser's agency on file (what chat's quote used) is picked.
 */
export function draftFromChatDraft(doc: MediaPlanDraftDoc, registry: { agencies: readonly MediaPlanAgency[]; advertiserAgencyKey?: string }): PlanDraft {
  const r = doc.request;
  const { segments, idFor } = segmentsFromRequest(r);
  const pricing = r.pricing;
  for (const s of segments) {
    for (const [unitKey, cpm] of Object.entries(pricing?.unitCpm || {})) {
      if (typeof cpm === 'number') s.rates[unitKey as MediaPlanUnitKey] = { value: String(cpm), origin: 'typed' };
    }
  }
  for (const [segmentKey, units] of Object.entries(pricing?.segmentUnitCpm || {})) {
    const segment = segments.find((s) => s.id === idFor(segmentKey));
    for (const [unitKey, cpm] of Object.entries(units || {})) {
      if (segment && typeof cpm === 'number') segment.rates[unitKey as MediaPlanUnitKey] = { value: String(cpm), origin: 'typed' };
    }
  }
  const agency = r.agencyKey
    ? { agencyKey: draftAgencyKey(r.agencyKey), agencyOther: '' }
    : doc.agencyName?.trim()
      ? agencyChoiceForName(doc.agencyName, registry.agencies)
      : { agencyKey: draftAgencyKey(registry.advertiserAgencyKey), agencyOther: '' };
  return {
    advertiserKey: r.advertiserKey || '',
    advertiserName: r.advertiserName,
    ...agency,
    ...sharedFields(r),
    segments,
    upchargesMode: pricing?.upchargesMode ?? 'none',
    reasons: {},
  };
}
